import { isEmpty, isEqual } from 'lodash';
import { FormikHelpers, FormikProps } from 'formik';
import React, { useContext, useEffect } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { PersonUpdate } from './person-update';
import { Person } from '../../../../global/models/person-model';
import { parseChanged, getChangedAddress, parsePhone, appendDocument } from '../../../../global/helpers/form-data-helper';
import { patchPersonInfo } from '../../../../global/ducks/person-prc-api';
import { setCurrentPerson, setPatchPersonStatus, togglePublishedAddress } from './personal-info-slice';
import { apiErrorClear } from '../../../../global/ducks/base-api';
import { ApiStatuses } from '../../../../global/models/api-model';
import { setDocumentUploadStatus, toggleDocumentUpload } from './document-upload-slice';
import { DemographicsContext } from '../../../../global/models/demographics-model';
import { TcsmEpiApiState, TcsmStates } from '../../../../root/tcsm-model';
import { processRequestWithDocumentUpload } from './document-upload-request';
import { DocumentUploadState } from './document-upload-models';
import { AnnualEnrollmentContext } from '../../../annual-enrollment/models/annual-enrollment-models';
import { AccountInfoRoutes } from '../../constants/routes';
import { requiredDocUploadFieldChanged } from '../../../../global/helpers/doc-upload-helpers';
import { PersonalFormValues } from './personal-form-models';
import { completePersonType } from '../../helpers/personal-info-helpers';
import { ProcessName, RegisterStartProcess, RegisterCompletedProcess } from '../../../../global/helpers/analytics-helper';
import { PersonalInfoWizard } from './wizard/personal-info-wizard';
import { setCurrentNewHireBenefitEnrollmentStep } from '../../../arc/features/new-hire-enrollment/slice/new-hire-enrollment-slices';
import { BenefitEnrollmentStep } from '../../../arc/features/new-hire-enrollment/models/pension-enrollment-models';
import { NewHireEnrollmentRoutes } from '../../../arc/features/new-hire-enrollment/models/new-hire-routes';

interface Props {
	basePath?: string;
	customPersonalInfoWizard?: PersonalInfoWizard;
	isDioApprover?: boolean;
	docUpload?: boolean;
}

export function PersonalInformationApp(props: Props) {
	const app = useContext(DemographicsContext);
	const context = useContext(AnnualEnrollmentContext);
	const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const person = useSelector<TcsmStates, Person>(state => state.personalInfo[app]?.currentPerson);
	const docUploadState = useSelector<TcsmStates, DocumentUploadState>(state => state.documentUpload[app]);

	const docUploadInternationalWarning = docUploadState?.showInternationalWarning;
	const docUploadDemographicWarning = docUploadState?.showDemographicWarning;
	const minDocumentsUploadRequired = docUploadInternationalWarning && docUploadDemographicWarning ? 2 : 1;

	const history = useHistory();
	const dispatch = useDispatch();
	const personalInfoWizard = props.customPersonalInfoWizard || new PersonalInfoWizard(epiProps, context, app, dispatch, history);
	RegisterStartProcess(ProcessName.PersonalInformation);

	useEffect(() => {
		dispatch(apiErrorClear());
		dispatch(setPatchPersonStatus(ApiStatuses.None, app));
	}, [app]);

	useEffect(() => {
		personalInfoWizard.useEffectDispatch();
	}, []);

	if (!epiProps.personalInfoSettingState.contentLoaded || !person) {
		return (<p>Loading...</p>);
	}


	const onSuccess = (response, formikHelpers: FormikHelpers<PersonalFormValues>) => {
		if (docUploadState?.stagedDocuments?.length > 0) {
			history.push({ search: '?docUpload=true' });
		}
		dispatch((setCurrentPerson(response, app)));
		dispatch((setPatchPersonStatus(ApiStatuses.Success, app)));
		dispatch(setDocumentUploadStatus(false, app));
		dispatch(toggleDocumentUpload(false, app));

		formikHelpers.resetForm();

		RegisterCompletedProcess(ProcessName.PersonalInformation);

		if (props.isDioApprover) {
			dispatch(setCurrentNewHireBenefitEnrollmentStep(BenefitEnrollmentStep.RELATIONSHIP));
		}

		personalInfoWizard.onSuccessRedirect(props.basePath);
	};

	const onSuccessNoChanges = (formikHelpers: FormikHelpers<PersonalFormValues>) => {

		dispatch((setPatchPersonStatus(ApiStatuses.NoChange, app)));
		dispatch(setDocumentUploadStatus(false, app));
		dispatch(toggleDocumentUpload(false, app));

		formikHelpers.resetForm();

		RegisterCompletedProcess(ProcessName.PersonalInformation);

		personalInfoWizard.onSuccessNoChanges(props.basePath);
	};

	const onDocUploadSuccess = (toUpdate: any, formikHelpers: FormikHelpers<PersonalFormValues>) => (data) => {
		const withDocuments = appendDocument(toUpdate, data?.map(a => a?.data?.doc_id));
		dispatch(patchPersonInfo(person.person_guid, withDocuments, !props?.docUpload, (response) => onSuccess(response, formikHelpers)));
	};

	const onSubmitHandler = (update: PersonalFormValues, initialValues: PersonalFormValues, formikHelpers: FormikProps<PersonalFormValues>) => {

		const hasNoChanges = isEqual(initialValues, update);
		const withPhoneUpdate = parsePhone(update);
		const toUpdate = parseChanged(initialValues, withPhoneUpdate, true);

		completePersonType(toUpdate, person?.person_guid);

		if ((docUploadState.documentsUploadedCount < minDocumentsUploadRequired) &&
			(requiredDocUploadFieldChanged(toUpdate, initialValues, !props?.docUpload) || docUploadInternationalWarning)) {
			dispatch(toggleDocumentUpload(true, app, docUploadInternationalWarning));
			return;
		}

		if (person.cleric_in_book === 'Y' && Object.keys(toUpdate)?.filter(a => a.includes('mailing')).length > 0) {
			dispatch(togglePublishedAddress(true,
				getChangedAddress(initialValues, update, person),
				toUpdate,
				app,
			));
			return;
		}

		dispatch(apiErrorClear());
		dispatch((setPatchPersonStatus(ApiStatuses.None, app)));

		if (!isEmpty(toUpdate)) {
			if (docUploadState.stagedDocuments && docUploadState.stagedDocuments.length > 0 && docUploadState.showDocumentUpload) {
				processRequestWithDocumentUpload(docUploadState, person.person_guid, dispatch, onDocUploadSuccess(toUpdate, formikHelpers));
			} else if (!personalInfoWizard.isEnrollmentForm() && hasNoChanges) {
				onSuccessNoChanges(formikHelpers);
			} else {
				dispatch(patchPersonInfo(person.person_guid, toUpdate, !props?.docUpload, (response) => onSuccess(response, formikHelpers)));
			}
		} else if (personalInfoWizard.isEnrollmentForm()) {
			// no changes made and annual enrollment, continue
			history.push(`${props.basePath}${props.basePath?.includes(NewHireEnrollmentRoutes.EmployeeRecord) ? AccountInfoRoutes.EmploymentSegment : AccountInfoRoutes.RelationshipsSegment}`);
		} else {
			onSuccessNoChanges(formikHelpers);
		}
	};

	const oncancelHandler = () => {
		if (props.isDioApprover) {
			dispatch(setCurrentNewHireBenefitEnrollmentStep(BenefitEnrollmentStep.REVIEW));
		}
		window.scrollTo(0, 0);
		dispatch(toggleDocumentUpload(false, app));
		personalInfoWizard.onCancelRedirect(props.basePath, props?.isDioApprover);
	};



	return (
		<>
			{
				personalInfoWizard?.renderAlerts()
			}
			<PersonUpdate
				formHeader={personalInfoWizard.getPageHeader()}
				formDescription={personalInfoWizard.getPageDescription()}
				currentPerson={person}
				labelSetting={epiProps.labelSettingState.epiContent}
				onSubmitHandler={onSubmitHandler}
				isEnrollmentForm={personalInfoWizard.isEnrollmentForm()}
				isAnnualEnrollmentForm={personalInfoWizard.isAnnualEnrollmentForm()}
				gpsType={personalInfoWizard.getGpsType()}
				hasMaritalStatusSection={personalInfoWizard.hasMaritalStatusSection()}
				submitButtonText={personalInfoWizard.getSubmitButtonText()}
				cancelButtonText={personalInfoWizard.getCancelButtonText()}
				preventButtonDisableValidation={personalInfoWizard.preventButtonDisableValidation()}
				onCancelHandler={oncancelHandler}
				basePath={props.basePath}
				docUpload={props?.docUpload}
			/>
		</>
	);
}

PersonalInformationApp.defaultProps =
{
	basePath: '',
	customPersonalInfoWizard: null,
	isDioApprover: false,
	docUpload: true,
};